import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { QuestionBase } from '@jump-tech-frontend/domain';
import { ImageAsset } from '../../../domain/types';
import { ImageAssetFactory } from '../../../domain/card';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import { BaseQuestionComponent } from '../../question.component';
import { ScrollService } from '../../scroll.service';
import { untilDestroyed } from '@jump-tech-frontend/angular-common';
import { ImageAssetComponent } from '../../image-asset.component';
import { QuestionHintComponent } from '@jump-tech-frontend/question-components';
import { QuestionLabelContentComponent } from '@jump-tech-frontend/question-components';
import { DateQuestionService } from './date-question.service';

@Component({
  selector: `crds-question-date`,
  template: `
    @if(show && form) {
    <!-- QUESTION LABEL -->
    @if (question.label) {
    <label class="form-label" [attr.for]="question.key" [attr.data-qa]="question.key">
      <question-label-content [question]="question" [isInvalid]="isInvalid" [i18ns]="i18ns"></question-label-content>
    </label>
    }

    <!-- QUESTION INPUT -->
    @if (isValueIso) {
    <div [formGroup]="displayForm">
      <input
        [formControlName]="question.key + '__display'"
        [attr.data-qa]="question.key + 'Input'"
        class="form-control mb-2"
        type="date"
        max="9999-12-31"
        #dateInput
      />
    </div>
    <div [formGroup]="form">
      <input [id]="question.key" [formControlName]="question.key" type="hidden" />
    </div>
    } @else {
    <div [formGroup]="form">
      <input
        [id]="question.key"
        [formControlName]="question.key"
        [attr.data-qa]="question.key + 'Input'"
        class="form-control mb-2"
        type="date"
        max="9999-12-31"
        #dateInput
      />
    </div>
    }

    <!-- QUESTION HINT -->
    <question-hint [question]="question"></question-hint>

    <!-- QUESTION IMAGE -->
    <div class="image-wrapper">
      <crds-image-asset [images]="images" [key]="question.key"></crds-image-asset>
    </div>
    }
  `,
  imports: [ReactiveFormsModule, QuestionLabelContentComponent, QuestionHintComponent, ImageAssetComponent]
})
export class DateQuestionComponent extends BaseQuestionComponent implements OnChanges {
  @Input() override form: UntypedFormGroup;
  @Input() override question: QuestionBase<string>;
  @Input() i18ns: I18nKeys;
  @ViewChild('dateInput') input: ElementRef;

  images: ImageAsset[];
  isValueIso = false;
  displayForm: UntypedFormGroup;

  private untilDestroyed = untilDestroyed();

  constructor(
    private imageAssetFactory: ImageAssetFactory,
    private scrollService: ScrollService,
    private dateQuestionService: DateQuestionService
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const currentValue = this.form.get(this.question.key)?.value;
    this.isValueIso = this.dateQuestionService.valueIsDateIso(currentValue);

    if (this.isValueIso) {
      this.buildDateDisplayForm();
    }

    if (!this.question || !Object.prototype.hasOwnProperty.call(changes, 'question')) {
      return;
    }

    if (this.question.disabled === true && !this.show) {
      setTimeout(() => this.form?.get(this.question.key)?.disable());
    }

    this.images = this.imageAssetFactory.create(this.question.image, this.form);

    this.scrollService.scrollObservable.pipe(this.untilDestroyed()).subscribe(documentId => {
      if (documentId === this.question.key) {
        this.input.nativeElement.scrollIntoView();
      }
    });
  }

  private buildDateDisplayForm(): void {
    const isoValue = this.form.get(this.question.key)?.value;
    this.displayForm = this.dateQuestionService.buildDateDisplayForm(this.question.key, isoValue, this.form);
  }

  override get isInvalid(): boolean {
    return !!(this.form?.get(this.question.key)?.touched && this.form?.get(this.question.key)?.invalid);
  }

  override get show(): boolean {
    const showIfQuestionPopulated = this.question.showIf ? this.showIf : true;
    return (
      (this.question.showIfPopulated !== true || this.value) && showIfQuestionPopulated && this.question.show !== false
    );
  }
}
